import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '@/lib/errorReporting';
import { FeedbackDialog } from '@/components/feedback/FeedbackDialog';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** id da ocorrência de erro enviada — vincula o relato do usuário */
  erroId: string | null;
  isFeedbackOpen: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, erroId: null, isFeedbackOpen: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Anexa a pilha de componentes para a Triagem localizar a tela com erro
    const enriched = new Error(error.message);
    enriched.stack = `${error.stack ?? ''}
Component stack:${errorInfo.componentStack ?? ''}`;
    void reportClientError(enriched).then((erroId) => {
      if (erroId) this.setState({ erroId });
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[50vh] items-center justify-center p-8">
          <div className="max-w-md card p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
              <svg className="h-6 w-6 text-danger" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="mb-2 text-lg font-semibold text-ink">
              Algo deu errado
            </h2>
            <p className="mb-4 text-sm text-muted">
              Ocorreu um erro inesperado e a equipe já foi avisada automaticamente. Recarregue a página para continuar.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn-primary"
              >
                Recarregar página
              </button>
              <button
                type="button"
                onClick={() => this.setState({ isFeedbackOpen: true })}
                className="btn-secondary"
                aria-haspopup="dialog"
              >
                Contar o que aconteceu
              </button>
            </div>
            <p className="mt-3 text-xs text-muted">
              Descrever o que você estava fazendo ajuda a corrigir mais rápido.
            </p>
          </div>
          <FeedbackDialog
            isOpen={this.state.isFeedbackOpen}
            onClose={() => this.setState({ isFeedbackOpen: false })}
            prefill={{
              tipo: 'bug',
              erroMensagem: this.state.error?.message,
              ...(this.state.erroId ? { erroId: this.state.erroId } : {}),
            }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
